import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { postUnstake } from '~/apis'
import { StyledFullPageModalContentOverlay } from '~/components/FullPageModal/styled'
import Spin from '~/components/Loading/Spin'
import { message } from '~/components/Message'
import TradeDetail from '~/components/TradeDetail'
import { TRADE_STATUS, TRADE_TYPE } from '~/components/TradeDetail/types'
import { useModalToggle, useFeeData } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import CommonTipsContent from './CommonTipsContent'
import { StyledLonRedeemContainer } from '../styled'
import { gasProtect } from '~/utils/validate'
import { useGA, useLONStakingContract, useV6SDK } from '~/hooks'
import { calcGasFee } from '~/components/Exchange/helpers'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import { gasSpeedup } from '~/utils/gasSpeedup'

interface Props {
  onComplete: () => void
}

const LonNoticeBeforeRedeem: React.FC<Props> = ({ onComplete }) => {
  const { t } = useTranslation()
  const { signer } = useV6SDK()
  const toggleModalRedemption = useModalToggle(ApplicationModal.LON_REDEMPTION)
  const ethBalance = useNativeTokenBalance()
  const feeData = useFeeData()
  const LONStakingContract = useLONStakingContract()

  const [txHash, setTxHash] = useState('')
  const [status, setStatus] = useState<TRADE_STATUS>()
  const { trackClick } = useGA()

  const estimateGas = async () => {
    try {
      const gasLimit = await LONStakingContract.estimateGas.unstake()
      return gasSpeedup(gasLimit)
    } catch (error) {}
  }

  const handleTriggerUnlock = async () => {
    const gasPrice = feeData.gasPrice
    const gasLimit = await estimateGas()
    if (!gasLimit) {
      message.error(t('tokenlon_network_request_failed'))
      return
    }
    const fee = calcGasFee(gasPrice.toString(), gasLimit?.toString())
    const enough = ethBalance.isGreaterThan(fee)
    if (!enough) {
      message.error(t('provider_error_code_INSUFFICIENT_FUNDS'))
      return
    }
    setStatus(TRADE_STATUS.UN_BROADCAST)
    try {
      const hash = await postUnstake(signer, gasProtect)
      if (hash) {
        setTxHash(hash)
        setStatus(TRADE_STATUS.PENDING)
      }
      try {
        const receipt = await signer.publicClient.waitForTransactionReceipt({
          hash,
        })
        if (receipt.status) {
          setStatus(TRADE_STATUS.SUCCESS)
          trackClick('webtl_staking_cooldown', { is_success: 1 })
        }
      } catch (e) {
        setStatus(TRADE_STATUS.FAILED)
        const errorMessage =
          typeof e === 'string' ? e : e?.reason || e?.message || e?.code
        if (errorMessage?.toLowerCase()?.includes('user rejected')) {
          message.error(t('user_rejected_message'))
        } else {
          message.error(errorMessage)
        }
        trackClick('webtl_staking_cooldown', {
          is_success: 0,
          message: errorMessage,
        })
      }
    } catch (e) {
      setStatus(undefined)
      toggleModalRedemption()
      const errorMessage =
        typeof e === 'string' ? e : e?.reason || e?.message || e?.code
      if (errorMessage?.toLowerCase()?.includes('user rejected')) {
        message.error(t('user_rejected_message'))
      } else {
        message.error(errorMessage)
      }
      trackClick('webtl_staking_cooldown', {
        is_success: 0,
        message: errorMessage,
      })
    }
  }

  if (status && status !== TRADE_STATUS.UN_BROADCAST) {
    return (
      <TradeDetail
        fromToken={t('unlock')}
        sectionData={[]}
        toToken=""
        tradeType={TRADE_TYPE.STAKING}
        txHash={txHash}
        onSuccess={onComplete}
        status={status}
      />
    )
  }

  return (
    <StyledLonRedeemContainer>
      <p className="redeem-title">{t('redeem_lon_notice')}</p>
      <section className="description">
        <p>{t('redeem_lon_notice_tips_1')}</p>
        <p>{t('redeem_lon_notice_tips_2')}</p>
        <p>{t('redeem_lon_notice_tips_3')}</p>
      </section>
      <CommonTipsContent />
      <div className="bottom-area">
        <button className="triger-unlock-btn" onClick={handleTriggerUnlock}>
          {!!status ? <Spin /> : t('trigger_cooldown_period')}
        </button>
      </div>
      {!!status && <StyledFullPageModalContentOverlay />}
    </StyledLonRedeemContainer>
  )
}

export default LonNoticeBeforeRedeem
